import type { SubjectKey } from '@/archive/constants/subject';
import {
    buildDesktopPageUrl,
    createOrganizationNode,
    ORGANIZATION_ID,
    withFragment,
    type JsonLdGraph,
} from './common';

type SubjectStructuredDataConfig = {
    courseFragment: string;
    description: string;
    educationalLevel: string;
    name: string;
    path: string;
    teaches: string[];
};

const createSubjectStructuredData = ({
    courseFragment,
    description,
    educationalLevel,
    name,
    path,
    teaches,
}: SubjectStructuredDataConfig): JsonLdGraph => {
    const subjectUrl = buildDesktopPageUrl(path);
    const courseId = withFragment(subjectUrl, courseFragment);

    return {
        '@context': 'https://schema.org',
        '@graph': [
            createOrganizationNode(),
            {
                '@type': 'WebPage',
                '@id': withFragment(subjectUrl, 'webpage'),
                name,
                url: subjectUrl,
                description: `${name} 과목 상세 페이지`,
                inLanguage: 'ko-KR',
                mainEntity: {
                    '@id': courseId,
                },
            },
            {
                '@type': 'Course',
                '@id': courseId,
                name: `${name} 커리큘럼`,
                description,
                educationalLevel,
                teaches,
                provider: {
                    '@id': ORGANIZATION_ID,
                },
            },
        ],
    };
};

export const subjectStructuredDataByKey: Partial<Record<SubjectKey, JsonLdGraph>> =
    {
        kor: createSubjectStructuredData({
            courseFragment: 'kor-course',
            description:
                '언어 자극, 통문자, 자음과 모음, 문장 읽기, 문장 쓰기, 문법, 독해력까지 단계적으로 학습하는 커리큘럼입니다.',
            educationalLevel: '유아, 예비초, 초등 저학년',
            name: '윙크 한글·국어',
            path: '/subject/kor',
            teaches: [
                '한글',
                '국어',
                '어휘력',
                '표현력',
                '문장 읽기',
                '문장 쓰기',
                '기초 문법',
                '독해력',
            ],
        }),
        math: createSubjectStructuredData({
            courseFragment: 'math-course',
            description:
                '수학의 기초를 쌓고 생활 속 수학적 사고를 키울 수 있도록 수학의 6대 영역과 연산력 강화, 초등 준비 학습을 단계적으로 제공하는 커리큘럼입니다.',
            educationalLevel: '유아, 예비초, 초등 저학년',
            name: '윙크 수학·연산',
            path: '/subject/math',
            teaches: [
                '수학',
                '연산',
                '인지 능력',
                '수 개념',
                '공간과 도형',
                '측정',
                '규칙성',
                '확률과 통계',
                '초등 준비 학습',
            ],
        }),
        en: createSubjectStructuredData({
            courseFragment: 'english-course',
            description:
                '스토리 영어, 파닉스, 영단어, 영어 동화와 노래를 통해 영어 듣기, 읽기, 말하기 기초를 단계적으로 학습하는 커리큘럼입니다.',
            educationalLevel: '유아, 예비초, 초등 저학년',
            name: '윙크 영어·파닉스',
            path: '/subject/en',
            teaches: [
                '영어',
                '파닉스',
                '영단어',
                '스토리 영어',
                '영어 듣기',
                '영어 읽기',
                '영어 말하기',
                '영어 동화',
                '영어 노래',
            ],
        }),
    };
